import { useNavigate } from "react-router-dom";
import { Sparkles, Activity, HeartPulse, Droplet, Brain, TrendingUp, ShieldCheck, ChevronRight, FileText, Watch } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";

const RISKS = [
  { label: "Cardiac", level: "Low", color: "#16A34A", icon: HeartPulse, pct: 18 },
  { label: "Diabetes", level: "Moderate", color: "#F59E0B", icon: Droplet, pct: 42 },
  { label: "Stress", level: "Low", color: "#16A34A", icon: Brain, pct: 25 },
];

const INSIGHTS = [
  { title: "Stay hydrated", desc: "Aim for 2.5L water today to support kidney health.", icon: Droplet, color: "#0EA5E9" },
  { title: "Move more", desc: "You're 2,000 steps below your weekly average.", icon: Activity, color: "#16A34A" },
  { title: "Book a check-up", desc: "It's been a while since your last full-body test.", icon: FileText, color: "#7C3AED" },
];

export default function TrataAI() {
  const navigate = useNavigate();
  const displayName = useAuthStore((s) => s.displayName());
  const score = 78;

  return (
    <div className="max-w-3xl space-y-6" data-testid="trata-ai-page">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-[#7C3AED]" />
          <h1 className="text-2xl md:text-3xl font-semibold">Trata AI</h1>
        </div>
        <p className="text-muted-foreground mt-1">Your AI Health Command Center, {displayName}.</p>
      </div>

      {/* Health score */}
      <Card className="rounded-3xl p-6 bg-gradient-to-br from-[#7C3AED0D] to-[#059669140] border-border">
        <div className="flex items-center gap-6">
          <div className="relative h-28 w-28 shrink-0">
            <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted))" strokeWidth="10" />
              <circle cx="50" cy="50" r="42" fill="none" stroke="#059669" strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 264} 264`} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-foreground">{score}</span>
              <span className="text-xs text-muted-foreground">/100</span>
            </div>
          </div>
          <div>
            <p className="text-lg font-semibold">Your Health Score is good</p>
            <p className="text-sm text-muted-foreground mt-1">Based on your profile, records & activity. Small daily habits can push it higher.</p>
            <Button onClick={() => navigate("/assistant")} className="mt-3 rounded-2xl bg-[#7C3AED]" data-testid="trata-ai-ask">
              <Sparkles className="h-4 w-4 mr-1.5" /> Ask Dr Sanjeevani
            </Button>
          </div>
        </div>
      </Card>

      {/* Risk predictions */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><TrendingUp className="h-5 w-5 text-[#059669]" /> Risk Predictions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {RISKS.map((r) => {
            const RIcon = r.icon;
            return (
              <Card key={r.label} className="rounded-2xl p-4 border-border">
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: r.color + "1A" }}>
                    <RIcon className="h-5 w-5" style={{ color: r.color }} />
                  </div>
                  <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: r.color + "1A", color: r.color }}>{r.level}</span>
                </div>
                <p className="mt-3 font-semibold text-sm">{r.label}</p>
                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${r.pct}%`, backgroundColor: r.color }} />
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* AI insights */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><Sparkles className="h-5 w-5 text-[#7C3AED]" /> AI Insights</h2>
        <Card className="rounded-2xl divide-y divide-border overflow-hidden border-border">
          {INSIGHTS.map((i) => {
            const IIcon = i.icon;
            return (
              <button key={i.title} onClick={() => navigate("/assistant")} className="w-full flex items-center gap-3 p-4 hover:bg-accent transition-colors text-left" data-testid={`insight-${i.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: i.color + "1A" }}>
                  <IIcon className="h-5 w-5" style={{ color: i.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{i.title}</p>
                  <p className="text-xs text-muted-foreground">{i.desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            );
          })}
        </Card>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/daily-tracker")} className="flex items-center gap-3 bg-card rounded-2xl border border-border p-4 hover:bg-accent transition-colors" data-testid="trata-ai-tracker">
          <Watch className="h-5 w-5 text-[#0EA5E9]" /> <span className="font-medium text-sm">Daily Tracker</span>
        </button>
        <button onClick={() => navigate("/records")} className="flex items-center gap-3 bg-card rounded-2xl border border-border p-4 hover:bg-accent transition-colors" data-testid="trata-ai-records">
          <FileText className="h-5 w-5 text-[#EC4899]" /> <span className="font-medium text-sm">Health Records</span>
        </button>
      </div>

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Insights are AI-generated and not a medical diagnosis.
      </p>
    </div>
  );
}
